
import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { 
  Users, 
  Building2, 
  UserRoundCog, 
  UserRound, 
  PlusCircle,
  Search,
  Edit2,
  Trash2,
  MoreHorizontal,
  ShieldPlus
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";

// Mock data para cada seção
const clientesMock = [
  { id: '1', nome: 'Maria Silva', documento: '123.456.789-00', email: 'maria@exemplo.com', telefone: '(11) 98765-4321', cidade: 'São Paulo' },
  { id: '2', nome: 'João Comércio Ltda', documento: '12.345.678/0001-90', email: 'contato@joao.com', telefone: '(21) 3456-7890', cidade: 'Rio de Janeiro' },
];

const fornecedoresMock = [
  { id: '1', nome: 'Suprimentos Elétricos SA', documento: '23.456.789/0001-12', email: 'vendas@suprimentos.com', telefone: '(31) 3456-7890', contato: 'Roberto' },
  { id: '2', nome: 'Distribuidora de Energia Ltda', documento: '34.567.890/0001-23', email: 'comercial@distrib.com', telefone: '(41) 4567-8901', contato: 'Amanda' },
];

const sociosMock = [
  { id: '1', nome: 'Carlos Eduardo', cpf: '987.654.321-00', participacao: 60, email: 'carlos@exemplo.com' },
  { id: '2', nome: 'Ana Beatriz', cpf: '876.543.210-99', participacao: 40, email: 'ana@exemplo.com' },
];

const funcionariosMock = [
  { id: '1', nome: 'Pedro Martins', cpf: '765.432.109-88', cargo: 'Técnico Solar', salario: 3500, 
    dadosBancarios: { banco: 'Banco Brasil', agencia: '1234', conta: '56789-0', tipo: 'Corrente' } },
  { id: '2', nome: 'Juliana Costa', cpf: '654.321.098-77', cargo: 'Assistente Administrativo', salario: 2800, 
    dadosBancarios: { banco: 'Caixa', agencia: '4321', conta: '98765-1', tipo: 'Poupança' } },
];

const CadastroTabContent: React.FC = () => {
  const [activeSection, setActiveSection] = useState("clientes");
  const [showClienteDialog, setShowClienteDialog] = useState(false);
  const [showFornecedorDialog, setShowFornecedorDialog] = useState(false);
  const [showSocioDialog, setShowSocioDialog] = useState(false);
  const [showFuncionarioDialog, setShowFuncionarioDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{id: string, nome: string, tipo: string} | null>(null);
  
  // Handlers para abrir formulários
  const handleNovoCliente = () => setShowClienteDialog(true);
  const handleNovoFornecedor = () => setShowFornecedorDialog(true);
  const handleNovoSocio = () => setShowSocioDialog(true);
  const handleNovoFuncionario = () => setShowFuncionarioDialog(true);
  
  // Handler para edição (apenas notificação de exemplo)
  const handleEditar = (item: any, tipo: string) => {
    // Aqui abriríamos o diálogo de edição com os dados preenchidos
    toast.info(`Editando ${tipo}: ${item.nome}`);
    
    // Exemplos de como abrir os diálogos para edição
    if (tipo === 'cliente') setShowClienteDialog(true);
    if (tipo === 'fornecedor') setShowFornecedorDialog(true);
    if (tipo === 'sócio') setShowSocioDialog(true);
    if (tipo === 'funcionário') setShowFuncionarioDialog(true);
  };
  
  // Handler para exclusão
  const handleExcluir = (item: any, tipo: string) => {
    setItemToDelete({id: item.id, nome: item.nome, tipo});
    setShowDeleteDialog(true);
  };
  
  const confirmarExclusao = () => {
    if (itemToDelete) {
      toast.success(`${itemToDelete.tipo} ${itemToDelete.nome} excluído com sucesso!`);
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }
  };
  
  // Handler para salvar (apenas notificação de exemplo)
  const handleSalvar = (tipo: string) => {
    toast.success(`${tipo} salvo com sucesso!`);
    
    // Fechar o diálogo apropriado
    if (tipo === 'Cliente') setShowClienteDialog(false);
    if (tipo === 'Fornecedor') setShowFornecedorDialog(false);
    if (tipo === 'Sócio') setShowSocioDialog(false);
    if (tipo === 'Funcionário') setShowFuncionarioDialog(false);
  };
  
  // Componente para o menu de ações
  const AcoesMenu = ({ item, tipo }: { item: any, tipo: string }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <span className="sr-only">Abrir menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleEditar(item, tipo)}>
          <Edit2 className="mr-2 h-4 w-4" />
          Editar
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleExcluir(item, tipo)} 
          className="text-red-600"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Excluir
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-6">
      <div className="financeiro-card p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-financeiro-text-DEFAULT">Cadastro</h2>
            <p className="text-financeiro-text-light mt-1">Gerencie dados de clientes, fornecedores, sócios e funcionários</p>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
            {activeSection === "clientes" && (
              <Button 
                onClick={handleNovoCliente}
                className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark"
              >
                <PlusCircle className="h-4 w-4 mr-2" /> Novo Cliente
              </Button>
            )}
            {activeSection === "fornecedores" && (
              <Button 
                onClick={handleNovoFornecedor}
                className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark"
              >
                <PlusCircle className="h-4 w-4 mr-2" /> Novo Fornecedor
              </Button>
            )}
            {activeSection === "socios" && (
              <Button 
                onClick={handleNovoSocio}
                className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark"
              >
                <PlusCircle className="h-4 w-4 mr-2" /> Novo Sócio
              </Button>
            )}
            {activeSection === "funcionarios" && (
              <Button 
                onClick={handleNovoFuncionario}
                className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark"
              >
                <PlusCircle className="h-4 w-4 mr-2" /> Novo Funcionário
              </Button>
            )}
          </div>
        </div>

        <Tabs defaultValue="clientes" value={activeSection} onValueChange={setActiveSection} className="w-full">
          <TabsList className="mb-6 border-b border-financeiro-border-light">
            <TabsTrigger 
              value="clientes" 
              className={`px-4 py-2 border-b-2 transition-colors flex items-center ${
                activeSection === "clientes" 
                  ? "border-financeiro-accent-DEFAULT text-financeiro-accent-DEFAULT" 
                  : "border-transparent text-financeiro-text-light hover:text-financeiro-text-DEFAULT"
              }`}
            >
              <Users className="h-4 w-4 mr-2" /> Clientes
            </TabsTrigger>
            <TabsTrigger 
              value="fornecedores" 
              className={`px-4 py-2 border-b-2 transition-colors flex items-center ${
                activeSection === "fornecedores" 
                  ? "border-financeiro-accent-DEFAULT text-financeiro-accent-DEFAULT" 
                  : "border-transparent text-financeiro-text-light hover:text-financeiro-text-DEFAULT"
              }`}
            >
              <Building2 className="h-4 w-4 mr-2" /> Fornecedores
            </TabsTrigger>
            <TabsTrigger 
              value="socios" 
              className={`px-4 py-2 border-b-2 transition-colors flex items-center ${
                activeSection === "socios" 
                  ? "border-financeiro-accent-DEFAULT text-financeiro-accent-DEFAULT" 
                  : "border-transparent text-financeiro-text-light hover:text-financeiro-text-DEFAULT"
              }`}
            >
              <UserRoundCog className="h-4 w-4 mr-2" /> Sócios
            </TabsTrigger>
            <TabsTrigger 
              value="funcionarios" 
              className={`px-4 py-2 border-b-2 transition-colors flex items-center ${
                activeSection === "funcionarios" 
                  ? "border-financeiro-accent-DEFAULT text-financeiro-accent-DEFAULT" 
                  : "border-transparent text-financeiro-text-light hover:text-financeiro-text-DEFAULT"
              }`}
            >
              <UserRound className="h-4 w-4 mr-2" /> Funcionários
            </TabsTrigger>
          </TabsList>
          
          {/* Conteúdo da aba Clientes */}
          <TabsContent value="clientes">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Clientes Cadastrados</h3>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Buscar cliente" 
                      className="pl-8 h-9 w-[250px]" 
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {clientesMock.map((cliente) => (
                  <Card key={cliente.id} className="border-l-4 border-l-financeiro-accent-DEFAULT">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row justify-between">
                        <div className="space-y-1">
                          <h4 className="font-medium">{cliente.nome}</h4>
                          <p className="text-sm text-gray-500">
                            {cliente.documento}
                          </p>
                          <p className="text-sm">
                            {cliente.email} | {cliente.telefone}
                          </p>
                          <p className="text-sm text-gray-500">
                            {cliente.cidade}
                          </p>
                        </div>
                        <div className="mt-4 md:mt-0 flex items-end">
                          <AcoesMenu item={cliente} tipo="cliente" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
          
          {/* Conteúdo da aba Fornecedores */}
          <TabsContent value="fornecedores">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Fornecedores Cadastrados</h3>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Buscar fornecedor" 
                      className="pl-8 h-9 w-[250px]" 
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {fornecedoresMock.map((fornecedor) => (
                  <Card key={fornecedor.id} className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row justify-between">
                        <div className="space-y-1">
                          <h4 className="font-medium">{fornecedor.nome}</h4>
                          <p className="text-sm text-gray-500">
                            CNPJ: {fornecedor.documento}
                          </p>
                          <p className="text-sm">
                            Contato: {fornecedor.contato} | {fornecedor.telefone}
                          </p>
                          <p className="text-sm text-gray-500">
                            {fornecedor.email}
                          </p>
                        </div>
                        <div className="mt-4 md:mt-0 flex items-end">
                          <AcoesMenu item={fornecedor} tipo="fornecedor" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
          
          {/* Conteúdo da aba Sócios */}
          <TabsContent value="socios">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Sócios Cadastrados</h3>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Buscar sócio" 
                      className="pl-8 h-9 w-[250px]" 
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {sociosMock.map((socio) => (
                  <Card key={socio.id} className="border-l-4 border-l-yellow-500">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row justify-between">
                        <div className="space-y-1">
                          <h4 className="font-medium">{socio.nome}</h4>
                          <p className="text-sm text-gray-500">
                            CPF: {socio.cpf}
                          </p>
                          <p className="text-sm">
                            Email: {socio.email}
                          </p>
                          <p className="text-sm font-medium">
                            Participação: {socio.participacao}%
                          </p>
                        </div>
                        <div className="mt-4 md:mt-0 flex items-end">
                          <AcoesMenu item={socio} tipo="sócio" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
          
          {/* Conteúdo da aba Funcionários */}
          <TabsContent value="funcionarios">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Funcionários Cadastrados</h3>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Buscar funcionário" 
                      className="pl-8 h-9 w-[250px]" 
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {funcionariosMock.map((funcionario) => (
                  <Card key={funcionario.id} className="border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row justify-between">
                        <div className="space-y-1">
                          <h4 className="font-medium">{funcionario.nome}</h4>
                          <p className="text-sm text-gray-500">
                            CPF: {funcionario.cpf}
                          </p>
                          <p className="text-sm">
                            Cargo: {funcionario.cargo}
                          </p>
                          <p className="text-sm">
                            Salário: {funcionario.salario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </p>
                          <p className="text-sm text-gray-500">
                            Banco: {funcionario.dadosBancarios.banco} | Ag: {funcionario.dadosBancarios.agencia} | Conta: {funcionario.dadosBancarios.conta}
                          </p>
                        </div>
                        <div className="mt-4 md:mt-0 flex items-end">
                          <AcoesMenu item={funcionario} tipo="funcionário" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Diálogo para cadastro de Cliente */}
      <Dialog open={showClienteDialog} onOpenChange={setShowClienteDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Cadastro de Cliente</DialogTitle>
            <DialogDescription>
              Preencha os dados do cliente para cadastro no sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="nome">Nome / Razão Social</Label>
                <Input id="nome" placeholder="Nome ou Razão Social" />
              </div>
              <div>
                <Label htmlFor="tipo-documento">Tipo</Label>
                <Select defaultValue="cpf">
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cpf">CPF</SelectItem>
                    <SelectItem value="cnpj">CNPJ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="documento">CPF/CNPJ</Label>
                <Input id="documento" placeholder="000.000.000-00" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="email@exemplo.com" />
              </div>
              <div>
                <Label htmlFor="telefone">Telefone</Label>
                <Input id="telefone" placeholder="(00) 00000-0000" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="endereco">Endereço</Label>
                <Input id="endereco" placeholder="Rua, número" />
              </div>
              <div>
                <Label htmlFor="cidade">Cidade</Label>
                <Input id="cidade" placeholder="Cidade" />
              </div>
              <div>
                <Label htmlFor="estado">Estado</Label>
                <Select defaultValue="sp">
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sp">São Paulo</SelectItem>
                    <SelectItem value="rj">Rio de Janeiro</SelectItem>
                    <SelectItem value="mg">Minas Gerais</SelectItem>
                    {/* Outros estados */}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea id="observacoes" placeholder="Observações adicionais (opcional)" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClienteDialog(false)}>Cancelar</Button>
            <Button onClick={() => handleSalvar("Cliente")} className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark">
              <ShieldPlus className="h-4 w-4 mr-2" /> Salvar Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo para cadastro de Fornecedor */}
      <Dialog open={showFornecedorDialog} onOpenChange={setShowFornecedorDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Cadastro de Fornecedor</DialogTitle>
            <DialogDescription>
              Preencha os dados do fornecedor para cadastro no sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="razao-social">Razão Social</Label>
                <Input id="razao-social" placeholder="Razão Social" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input id="cnpj" placeholder="00.000.000/0000-00" />
              </div>
              <div>
                <Label htmlFor="nome-contato">Nome do Contato</Label>
                <Input id="nome-contato" placeholder="Nome do contato principal" />
              </div>
              <div>
                <Label htmlFor="telefone-fornecedor">Telefone</Label>
                <Input id="telefone-fornecedor" placeholder="(00) 00000-0000" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="email-fornecedor">Email</Label>
                <Input id="email-fornecedor" type="email" placeholder="email@exemplo.com" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="endereco-fornecedor">Endereço</Label>
                <Input id="endereco-fornecedor" placeholder="Rua, número" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFornecedorDialog(false)}>Cancelar</Button>
            <Button onClick={() => handleSalvar("Fornecedor")} className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark">
              <ShieldPlus className="h-4 w-4 mr-2" /> Salvar Fornecedor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo para cadastro de Sócio */}
      <Dialog open={showSocioDialog} onOpenChange={setShowSocioDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Cadastro de Sócio</DialogTitle>
            <DialogDescription>
              Preencha os dados do sócio para cadastro no sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="nome-socio">Nome Completo</Label>
                <Input id="nome-socio" placeholder="Nome completo" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="cpf-socio">CPF</Label>
                <Input id="cpf-socio" placeholder="000.000.000-00" />
              </div>
              <div>
                <Label htmlFor="email-socio">Email</Label>
                <Input id="email-socio" type="email" placeholder="email@exemplo.com" />
              </div>
              <div>
                <Label htmlFor="telefone-socio">Telefone</Label>
                <Input id="telefone-socio" placeholder="(00) 00000-0000" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="participacao">Participação (%)</Label>
                <Input id="participacao" type="number" placeholder="0" min="0" max="100" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSocioDialog(false)}>Cancelar</Button>
            <Button onClick={() => handleSalvar("Sócio")} className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark">
              <ShieldPlus className="h-4 w-4 mr-2" /> Salvar Sócio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo para cadastro de Funcionário */}
      <Dialog open={showFuncionarioDialog} onOpenChange={setShowFuncionarioDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Cadastro de Funcionário</DialogTitle>
            <DialogDescription>
              Preencha os dados do funcionário para cadastro no sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="nome-funcionario">Nome Completo</Label>
                <Input id="nome-funcionario" placeholder="Nome completo" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="cpf-funcionario">CPF</Label>
                <Input id="cpf-funcionario" placeholder="000.000.000-00" />
              </div>
              <div>
                <Label htmlFor="cargo">Cargo</Label>
                <Input id="cargo" placeholder="Cargo/Função" />
              </div>
              <div>
                <Label htmlFor="salario">Salário</Label>
                <Input id="salario" type="number" decimalPrecision={2} placeholder="0,00" />
              </div>
              <div className="col-span-2">
                <Label className="text-base font-medium">Dados Bancários</Label>
              </div>
              <div>
                <Label htmlFor="banco">Banco</Label>
                <Input id="banco" placeholder="Nome do banco" />
              </div>
              <div>
                <Label htmlFor="agencia">Agência</Label>
                <Input id="agencia" placeholder="0000" />
              </div>
              <div>
                <Label htmlFor="conta">Conta</Label>
                <Input id="conta" placeholder="00000-0" />
              </div>
              <div>
                <Label htmlFor="tipo-conta">Tipo de Conta</Label>
                <Select defaultValue="corrente">
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corrente">Corrente</SelectItem>
                    <SelectItem value="poupanca">Poupança</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFuncionarioDialog(false)}>Cancelar</Button>
            <Button onClick={() => handleSalvar("Funcionário")} className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark">
              <ShieldPlus className="h-4 w-4 mr-2" /> Salvar Funcionário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o {itemToDelete?.tipo} {itemToDelete?.nome}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-500">
              Esta ação não pode ser desfeita.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmarExclusao}>
              <Trash2 className="h-4 w-4 mr-2" /> Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CadastroTabContent;
